import { useEffect, useState, useMemo, useCallback } from "react";
import { Amplify, API } from "aws-amplify";
import AWSConfig from "./aws-exports";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  FormControlLabel,
  TextField,
  Button,
  Switch,
  Box,
  Alert,
  Typography,
} from "@mui/material";
import "./App.css";
import { useAuth } from "./context/authContext";
import ProjectDialog from "./ProjectDialog";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const inactiveProjects = projects?.filter(
    (project) => project.Active === false
  );

  const [loading, setLoading] = useState(false);
  const [showInactiveProjects, setShowInactiveProjects] = useState(false);
  const [search, setSearch] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [sortKey, setSortKey] = useState("Project");
  const [reverse, setReverse] = useState(false);
  const { isAdmin } = useAuth();

  const filteredProjects = useMemo(() => {
    const projectsToFilter = showInactiveProjects ? inactiveProjects : projects;
    return projectsToFilter.filter(
      (item) =>
        (item.Project || "").toLowerCase().includes(search.toLowerCase()) &&
        (item.Active === true || showInactiveProjects)
    );
  }, [projects, inactiveProjects, search, showInactiveProjects]);

  // Sorting function using useMemo to optimize performance
  const sortedProjects = useMemo(() => {
    const sorted = [...filteredProjects].sort((a, b) => {
      if (sortKey === "Project" || sortKey === "SupportContact" || sortKey === "DataClassification" || sortKey === "Owner") {
        return (a[sortKey] || "").localeCompare(b[sortKey] || "");
      }
      if (sortKey === "Active" || sortKey === "Reports") {
        return a[sortKey] === b[sortKey] ? 0 : a[sortKey] ? -1 : 1;
      }
      return 0;
    });

    return reverse ? sorted.reverse() : sorted;
  }, [filteredProjects, sortKey, reverse]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setReverse(!reverse);
    } else {
      setSortKey(key);
      setReverse(false);
    }
  };

  const getSortIndicator = (key) => {
    if (sortKey === key) {
      return reverse ? "▼" : "▲"; // Up or down arrow
    }
    return "⇅"; // Default sorting indicator
  };

  const fetchData = async () => {
    setLoading(true);
    API.get("projects", "projects", {})
      .then((response) => {
        const data = response.projects;
        data.forEach((item, i) => {
          item.id = i + 1;
        });
        setProjects(data);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error.response);
      });
  };

  const handleRowClick = (item) => {
    setSelectedProject(item);
    setShowEditDialog(true);
  };

  const handleNewProjectClick = () => {
    setShowNewProjectDialog(true);
  };

  const handleNewProjectSave = useCallback((record) => {
    setShowNewProjectDialog(false);
    API.post("projects", "projects", { body: record })
      .then(fetchData)
      .catch((err) => console.error(err));
  }, []);

  const handleEditProjectSave = useCallback((record) => {
    setShowEditDialog(false);
    setSelectedProject(null);
    API.put("projects", "projects", { body: record })
      .then(fetchData)
      .catch((err) => console.error(err.response || err));
  }, []);

  const handleNewProjectClose = useCallback(() => {
    setShowNewProjectDialog(false);
  }, []);

  const handleEditProjectClose = useCallback(() => {
    setShowEditDialog(false);
    setSelectedProject(null);
  }, []);

  const handleSearch = (event) => {
    setSearch(event.target.value);
  };

  const toggleShowInactiveProjects = () => {
    setShowInactiveProjects(!showInactiveProjects);
  };

  useEffect(() => {
    Amplify.configure(AWSConfig);
    fetchData();
  }, []);

  if (!projects || loading) {
    return (
      <div className="px-5 py-5 container d-flex justify-content-center align-items-center text-primary">
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        ></div>
        <p className="ms-2 mb-0 fw-normal">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <div>
        {/* Top Navigation Bar (Replaces Bootstrap Table) */}
        <TableContainer
          component={Paper}
          sx={{
            p: 2,
            mb: 2,
          }}
        >
          <Table>
            <TableBody>
              <TableRow
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                {/* Search Input */}
                <TableCell
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: { xs: "100%", md: "33%" },
                    borderBottom: "none",
                  }}
                >
                  <label
                    htmlFor="search"
                    style={{
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Search by Project:
                  </label>
                  <TextField
                    id="search"
                    variant="outlined"
                    size="medium"
                    fullWidth
                    onChange={handleSearch}
                    sx={{
                      borderRadius: "8px",
                      fontSize: "1rem",
                      padding: "10px",
                    }}
                  />
                </TableCell>

                {/* New Project Button */}
                <TableCell
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    width: { xs: "100%", md: "33%" },
                    borderBottom: "none",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleNewProjectClick}
                    sx={{
                      backgroundColor: "#005EB8",
                      color: "#ffffff",
                      fontSize: "1rem",
                      padding: "12px 24px",
                      "&:hover": { backgroundColor: "#004a93" },
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    New Project
                  </Button>
                </TableCell>

                {/* Show Inactive Projects Toggle */}
                <TableCell
                  sx={{
                    flex: 1,
                    textAlign: "right",
                    display: "flex",
                    justifyContent: "flex-end",
                    width: { xs: "100%", md: "33%" },
                    borderBottom: "none",
                  }}
                >
                  <FormControlLabel
                    label="Show inactive projects"
                    sx={{
                      fontSize: "1rem",
                      whiteSpace: "nowrap",
                    }}
                    control={
                      <Switch
                        onChange={toggleShowInactiveProjects}
                        checked={showInactiveProjects}
                      />
                    }
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {projects.length !== 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("Project")}
                  >
                    Project {getSortIndicator("Project")}
                  </TableCell>
                  <TableCell>SpeedType</TableCell>
                  <TableCell
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleSort("DataClassification")}
                  >
                    DataClassification {getSortIndicator("DataClassification")}
                  </TableCell>
                  <TableCell
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("SupportContact")}
                  >
                    SupportContact {getSortIndicator("SupportContact")}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleSort("Owner")}
                  >
                    Owner {getSortIndicator("Owner")}
                  </TableCell>
                  <TableCell>DeployedBy</TableCell>
                  {showInactiveProjects && (
                    <TableCell
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() => handleSort("Active")}
                    >
                      Active {getSortIndicator("Active")}
                    </TableCell>
                  )}
                  </TableRow>
              </TableHead>

              {/* Table Body */}

              <TableBody>
                {sortedProjects.map((item) => (
                  <TableRow
                    key={item.id}
                    onClick={() => handleRowClick(item)}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <TableCell>{item.Project}</TableCell>
                    <TableCell>{item.SpeedType}</TableCell>
                    <TableCell>{item.DataClassification}</TableCell>
                    <TableCell>{item.SupportContact}</TableCell>
                    <TableCell>{item.Owner}</TableCell>
                    <TableCell>{item.DeployedBy}</TableCell>
                    {showInactiveProjects && (
                      <TableCell>{item.Active ? "true" : "false"}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box mt={4} mx="auto" maxWidth={600}>
            <Alert severity="error" variant="outlined">
              <Typography variant="body1" fontWeight={500}>
                No projects
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You are not involved in any projects.
              </Typography>
            </Alert>
          </Box>
        )}
      </div>
      <ProjectDialog
        open={showNewProjectDialog}
        onClose={handleNewProjectClose}
        onSave={handleNewProjectSave}
        projects={projects}
        isAdmin={isAdmin}
        mode="create"
      />
      <ProjectDialog
        key={selectedProject?.Project}
        open={showEditDialog}
        onClose={handleEditProjectClose}
        onSave={handleEditProjectSave}
        projects={projects}
        isAdmin={isAdmin}
        mode="edit"
        initialRecord={selectedProject}
      />
    </>
  );
};

export default Projects;
